/**
 * Ad-hoc project line helpers: form state ↔ API `project_products` payloads.
 * Inventory lines use `product_id`; ad-hoc lines use `product_id: null` and `line_*` fields.
 */

/**
 * Empty row for the "Items not in inventory" section in the project form.
 * @returns {Object}
 */
export function emptyAdHocFormRow() {
  return {
    id: null,
    line_name: '',
    line_cat_num: '',
    line_supplier_id: '',
    line_supplier_name: '',
    line_price: '',
    line_currency: '',
    quantity_needed: 1,
    status: 'NEEDS_QUOTE',
    notes: '',
  };
}

/**
 * Map a `ProjectProduct` from GET/detail into form row state.
 *
 * @param {Object} pp - Project product from API
 * @returns {Object}
 */
export function mapAdHocFromProjectProduct(pp) {
  const lineId = pp.id != null ? pp.id : null;
  return {
    id: lineId,
    line_name: pp.line_name || pp.display_name || '',
    line_cat_num: pp.line_cat_num || pp.display_cat_num || '',
    line_supplier_id: pp.line_supplier?.id || pp.line_supplier || '',
    line_supplier_name: pp.line_supplier_name || '',
    line_price: pp.line_price != null ? String(pp.line_price) : '',
    line_currency: pp.line_currency || '',
    quantity_needed: pp.quantity_needed || 1,
    status: pp.status || 'NEEDS_QUOTE',
    notes: pp.notes || '',
  };
}

/**
 * Build one ad-hoc `project_products` entry for POST/PATCH.
 *
 * @param {Object} row - Form row from {@link emptyAdHocFormRow}
 * @returns {Object}
 */
export function buildAdHocPayloadRow(row) {
  const supplierId = row.line_supplier_id
    ? parseInt(row.line_supplier_id, 10)
    : null;
  const payload = {
    product_id: null,
    line_name: (row.line_name || '').trim(),
    line_cat_num: (row.line_cat_num || '').trim(),
    line_supplier: Number.isInteger(supplierId) ? supplierId : null,
    line_supplier_name: supplierId ? '' : (row.line_supplier_name || '').trim(),
    line_price:
      row.line_price === '' || row.line_price == null
        ? null
        : String(row.line_price),
    line_currency: row.line_currency || null,
    quantity_needed: parseInt(row.quantity_needed, 10) || 1,
    status: row.status || 'NEEDS_QUOTE',
    notes: (row.notes || '').trim(),
  };
  const lineId = row.id != null ? parseInt(row.id, 10) : null;
  if (Number.isInteger(lineId) && lineId > 0) {
    payload.id = lineId;
  }
  return payload;
}

/**
 * Build ad-hoc payload array from form rows (non-empty names only).
 *
 * @param {Object[]} adHocLines
 * @returns {Object[]}
 */
export function buildAdHocPayload(adHocLines) {
  return (adHocLines || [])
    .filter(row => (row.line_name || '').trim())
    .map(buildAdHocPayloadRow);
}

/**
 * Client-side validation before submit (backend also validates).
 *
 * @param {Object[]} adHocPayload - Output of {@link buildAdHocPayload}
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateAdHocPayload(adHocPayload) {
  for (const row of adHocPayload) {
    if (!row.line_supplier && !row.line_supplier_name) {
      return {
        valid: false,
        message: 'Each custom line needs a supplier or supplier name',
      };
    }
  }
  return { valid: true };
}
